import { hasAccountsPayable } from '@avalara/bat-apps-shared/helpers';
import {
  AuiIcon,
  AuiSupernav,
  SAccordionContained,
  SAccordionStacked,
  SBox,
  SCol,
  SContainer,
  SRow,
} from '@avalara/skylab-sdk/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isOneWebDomainRuntime } from '../../helpers';
import analytics from '../../lib/analytics';
import { checkIsAuthenticated, getAccessToken } from '../../lib/helpers';
import { fetchAccountData, sendAPMarketingData } from '../../lib/side-effects/accounts';
import { getSystemErrorMessaging } from '../../lib/side-effects/error';
import { selectState } from '../../selectors';
import { setActiveFlow } from '../../store/system';
import ScheduleDialog from './schedule-dialog';
import './chilipiper-calendar.css';

import { t } from '@avalara/bat-apps-shared/i18n';
const styles = {
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
};

const { ChiliPiper } = window;

const AccountsPayable = () => {
  const dispatch = useDispatch();
  const {
    account: { accountId, obligations, customerAccountId, user, companyName },
    data: { isLoading },
    features,
  } = useSelector(selectState);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [schedulingInProgress, setSchedulingInProgress] = useState(false);

  useEffect(
    () => () => {
      if (typeof window.AvaApp?.removeAuiNav === 'function') {
        window.AvaApp.removeAuiNav();
      }
    },
    [],
  );

  useEffect(() => {
    dispatch(fetchAccountData());
    const isUserAuthenticated = checkIsAuthenticated();
    dispatch(setActiveFlow('accounts-payable'));

    if (!isUserAuthenticated) {
      dispatch(getSystemErrorMessaging({ errorCode: 'ineligibleForAccountsPayable' }));
    }
  }, []);

  useEffect(() => {
    if (ChiliPiper !== undefined) return;

    const script = document.createElement('script');
    script.src = 'https://avalara.chilipiper.com/concierge-js/cjs/concierge.js';
    script.defer = true;

    document.body.appendChild(script);
  }, []);

  if (!features.ACCOUNTSPAYABLE) return null;

  const accountsPayableObligation = hasAccountsPayable(obligations);

  const sendMarketingData = async () => {
    const accountInfo = {
      first_name: user?.firstName,
      last_name: user?.lastName,
      email: user?.email,
    };

    await dispatch(sendAPMarketingData(dispatch, accountInfo, companyName));
  };

  const handleOnClick = () => {
    setDialogOpen(true);
    setSchedulingInProgress(true);

    if (typeof window.ChiliPiper === 'undefined') {
      analytics.track('ChiliPiper is undefined');
    }

    window?.ChiliPiper?.submit('avalara', 'accounts-payable', {
      map: true,
      domElement: '#chilipiper-calendar',
      trigger: 'InAppButton',
      accountId: customerAccountId,
      lead: {
        PersonEmail: user?.email,
        PersonFirstName: user?.firstName,
        PersonLastName: user?.lastName,
      },
      onError: () => {
        dispatch(getSystemErrorMessaging({ errorCode: 'chiliPiperBookingError' }));
      },
      onSuccess: () => {
        setSchedulingInProgress(false);
        void sendMarketingData();
      },
      onClose: () => {
        window.location.reload();
      },
    });
  };

  const handleDismissDialog = () => {
    setDialogOpen(false);
  };

  const renderNavigation = () => {
    if (!accountId || isLoading) return null;
    if (isOneWebDomainRuntime() && typeof window.AvaApp?.renderAuiNav === 'function') window.AvaApp.renderAuiNav();

    return <AuiSupernav accountId={accountId.toString()} requestCurrentAccessToken={getAccessToken} />;
  };
  return (
    <>
      {renderNavigation()}
      {features.ACCOUNTSPAYABLE && !isLoading && (
        <SContainer data-ref='buy.accounts-payable.container'>
          <SRow>
            <SCol span='7' className='pad-top-lg'>
              <h1 data-ref='buy.accounts-payable.header'>{t('client.headings.consumer-use-tax')}</h1>
              <span className='font-semibold'>
                {t('client.general.simplify-consumer-use-tax-compliance-with-avatax-for-account')}
              </span>
              <p data-ref='buy.accounts-payable.description'>
                If your business pays for goods and services in the U.S., you&apos;re responsible for ensuring you pay
                the correct tax for those purchases. AvaTax for Accounts Payable makes it easier to stay compliant by
                helping you audit purchases, identify overpayment, and calculate and report consumer use tax.
              </p>
              <div
                data-ref='buy.accounts-payable.cta-container'
                className={`pad-top-lg ${accountsPayableObligation ? 'hidden' : ''}`}
              >
                <button
                  type='button'
                  className='primary'
                  data-ref='buy.accounts-payable.get-started-button'
                  onClick={handleOnClick}
                >
                  {t('client.buttons.get-avatax-for-accounts-payable')}
                </button>
              </div>
              <div>
                <ScheduleDialog
                  customerAccountId={customerAccountId}
                  dialogOpen={dialogOpen}
                  schedulingInProgress={schedulingInProgress}
                  handleDismissDialog={handleDismissDialog}
                />
              </div>
            </SCol>
          </SRow>
          <SRow data-ref='buy.accounts-payable.middle-section'>
            <SCol span='7'>
              <div className='pad-bottom-lg'>
                <h3>{t('client.headings.avoid-inaccurate-tax-payments')}</h3>
                <p>{t('client.general.when-vendors-charge-incorrect-sales-tax-or-apply-tax-on-exem')}</p>
              </div>
              <div className='pad-bottom-lg'>
                <h3>{t('client.headings.calculate-and-report-consumer-use-tax')}</h3>
                <p>
                  If you owe consumer use tax on purchases that were initially exempt from sales tax, we’ll help you
                  calculate your liability. You can then report and pay the tax on your sales and use tax return.
                </p>
              </div>
              <div>
                <h3>{t('client.headings.stay-audit-ready')}</h3>
                <p>{t('client.general.if-you-get-audited-for-sales-and-use-tax-you-can-generate-co')}</p>
              </div>
            </SCol>
            <SCol span='5'>
              <div className='flex flex-wrap' style={{ flexBasis: '400px' }}>
                <iframe
                  data-ref='buy.accounts-payable.video'
                  width='100%'
                  height='315'
                  src='https://www.youtube.com/embed/ietgNMPSTfU?si=FQ7bpLhxWmn70y5e'
                  title={t('client.accessibility.youtube-video-player')}
                  allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                  referrerPolicy='strict-origin-when-cross-origin'
                  allowFullScreen
                />
              </div>
            </SCol>
          </SRow>
          <SRow className='pad-top-lg'>
            <SCol className='pad-bottom-sm'>
              <h2>{t('client.headings.how-it-works')}</h2>
            </SCol>
          </SRow>
          <SRow>
            <SCol span='12'>
              <div style={styles.cardContainer} data-ref='buy.accounts-payable.product-card-container'>
                <SBox>
                  <div className='text-center pad-all-xl margin-all-md'>
                    <AuiIcon slot='media' name='integration' className='justify-content-center' />
                    <div className='text-center'>
                      <p>1. Connect purchase transactions through your existing integration or bulk import</p>
                    </div>
                  </div>
                </SBox>
                <SBox>
                  <div className='text-center pad-all-xl margin-all-md'>
                    <AuiIcon slot='media' name='software' className='justify-content-center' />
                    <div className='text-center'>
                      <p>{t('client.general.2-review-accuracy-of-purchase-transactions-and-approve-our-c')}</p>
                    </div>
                  </div>
                </SBox>
                <SBox>
                  <div className='text-center pad-all-xl margin-all-md'>
                    <AuiIcon slot='media' name='e-invoicing-and-live-reporting' className='justify-content-center' />
                    <div className='text-center'>
                      <p>{t('client.general.3-report-consumer-use-tax-on-your-sales-and-use-tax-returns')}</p>
                    </div>
                  </div>
                </SBox>
              </div>
            </SCol>
          </SRow>
          <SRow className='pad-top-xl'>
            <SCol>
              <SAccordionContained data-ref='buy.accounts-payable.faq-container'>
                <div slot='title'>
                  <h2>FAQ</h2>
                </div>
                <SAccordionStacked>
                  <h3 slot='title' data-ref='buy.accounts-payable.faq.learn-tax-differences-title'>
                    {t('client.headings.how-is-consumer-use-tax-different-from-sales-tax')}
                  </h3>
                  <div>
                    <p data-ref='buy.accounts-payable.faq.learn-tax-differences-description' className='pad-bottom-md'>
                      Sales tax is assessed at the time of purchase and remitted to the government by the seller.
                      Consumer use tax is self-assessed by the buyer after purchase. The buyer then reports and remits
                      tax to the government with a sales and use tax return.
                    </p>
                    <a
                      data-ref='buy.accounts-payable.faq.learn-tax-differences-link'
                      href='https://www.avalara.com/blog/en/north-america/2023/05/use-tax-sales-tax-everything-you-need-to-know.html'
                    >
                      {t('client.general.learn-more-about-the-differences-between-sales-tax-and-use-t')}
                    </a>
                  </div>
                </SAccordionStacked>
                <SAccordionStacked>
                  <h3 data-ref='buy.accounts-payable.faq.learn-use-tax-title' slot='title'>
                    {t('client.headings.what-creates-a-consumer-use-tax-compliance-obligation')}
                  </h3>
                  <div>
                    <p data-ref='buy.accounts-payable.faq.learn-use-tax-description' className='pad-bottom-md'>
                      {t('client.general.a-buyer-owes-consumer-use-tax-when-the-seller-does-not-colle')}
                    </p>
                    <a
                      data-ref='buy.accounts-payable.faq.learn-use-tax-link'
                      href='https://www.avalara.com/content/dam/assets/documents/document-managing-business-use-tax-obligations-and-compliance.pdf'
                    >
                      {t('client.general.learn-more-about-consumer-use-tax-rules')}
                    </a>
                  </div>
                </SAccordionStacked>
                <SAccordionStacked>
                  <h3 data-ref='buy.accounts-payable.faq.learn-audit-title' slot='title'>
                    {t('client.headings.how-is-consumer-use-tax-compliance-examined-in-an-audit')}
                  </h3>
                  <div>
                    <p data-ref='buy.accounts-payable.faq.learn-audit-description' className='pad-bottom-md'>
                      {t('client.general.in-a-sales-and-use-tax-audit-buyers-must-prove-all-tax-has-b')}
                    </p>
                    <a
                      data-ref='buy.accounts-payable.faq.learn-audit-link'
                      href='https://www.avalara.com/us/en/learn/whitepapers/sales-and-use-tax-audits.html'
                    >
                      {t('client.general.learn-more-about-what-to-expect-in-a-sales-and-use-tax-audit')}
                    </a>
                  </div>
                </SAccordionStacked>
                <SAccordionStacked>
                  <h3 data-ref='buy.accounts-payable.faq.schedule-call-title' slot='title'>
                    {t('client.headings.how-do-i-get-avatax-for-accounts-payable-for-my-business')}
                  </h3>
                  <div>
                    <p data-ref='buy.accounts-payable.faq.schedule-call-description' className='pad-bottom-md'>
                      Connect with an Avalara account representative to purchase and set up AvaTax for Accounts Payable.
                      We&apos;ll help you configure your company settings and add your purchase transactions using your
                      existing integration or by bulk import. Then you can begin using AvaTax for Accounts Payable!
                    </p>
                    <button
                      type='button'
                      className='link'
                      data-ref='buy.accounts-payable.faq.schedule-call-link'
                      onClick={handleOnClick}
                    >
                      {t('client.buttons.contact-an-account-representative-to-learn-more')}
                    </button>
                  </div>
                </SAccordionStacked>
              </SAccordionContained>
            </SCol>
          </SRow>
        </SContainer>
      )}
    </>
  );
};

export default AccountsPayable;
